import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

/**
 * Custom Event node (ENG-1062) — Unreal `UK2Node_CustomEvent` analog: a named,
 * user-defined event that acts as the START of an exec chain. It is fired BY
 * NAME (not by type) via EventRuntime.fireCustomEvent(name) — most notably from
 * a state-machine transition's Actions section. Same Events family / shell as
 * OnConstructNode; no exec input (events always start a chain), non-unique
 * (many allowed, each with its own name).
 *
 * data.name — the event name transitions (and future callers) fire by.
 */
export function CustomEventNode({ selected, data }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const named = !!(data?.name ?? '').trim()

  return (
    <div
      title="A named custom event. Fire it from a state-machine transition's Actions to run this chain."
      style={{
        background: selected ? '#280e0e' : '#1a0808',
        border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(named ? '#8b1a1a' : '#b06010') : named ? '#8b1a1a' : '#b06010'}`,
        borderRadius: 6,
        minWidth: 210,
        fontFamily: 'var(--m-font)',
        overflow: 'hidden',
        boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
      }}>
      {/* Header bar */}
      <div style={{
        background: '#5a1010',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontSize: 13 }}>⚡</span>
        <span style={{ color: '#ff9090', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Custom Event
        </span>
      </div>

      {/* Name */}
      <div style={{ padding: '8px 12px' }}>
        <div style={{ color: '#804040', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>EVENT NAME</div>
        <input className={nodeControlClass()}
          value={data?.name ?? ''}
          onChange={e => data.onChange?.('name', e.target.value)}
          onMouseDown={stop}
          placeholder="event name…"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#120404', border: `1px solid ${named ? '#402020' : '#7a4010'}`,
            color: '#ff9090', padding: '4px 7px',
            borderRadius: 3, fontSize: 10,
            fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
        {!named && (
          <div style={{ color: '#b06010', fontSize: 9, marginTop: 4 }}>
            Unnamed — a transition can only fire a named event.
          </div>
        )}
      </div>

      {/* Exec out — white square handle like Unreal */}
      <Handle
        type="source"
        position={Position.Right}
        id="execOut"
        style={{
          background: '#ddd',
          width: 12, height: 12,
          borderRadius: 2,
          border: '2px solid #aaa',
        }}
      />
    </div>
  )
}

/**
 * Call Custom Event — the action that fires a Custom Event by name from inside
 * the event graph (Unreal: calling a custom event). Runs every Custom Event
 * node with that name (EventRuntime.fireCustomEvent), each chain to its end,
 * then carries on from its own exec out. The name field suggests the graph's
 * custom events but takes any name, so a call can be wired before the event
 * it calls exists.
 *
 * data.name — the custom event to fire.
 */
export function CallCustomEventNode({ id, selected, data }) {
  const fired = isFired(data)
  const name = (data?.name ?? '').trim()
  const known = data?.customEventNames ?? []
  const listId = `cce-names-${id}`
  const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

  return (
    <div
      title="Fire the named Custom Event, run its chain, then continue."
      style={{
        background: '#1a0808',
        border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#8b1a1a') : '#8b1a1a'}`,
        borderRadius: 6,
        minWidth: 210,
        fontFamily: 'var(--m-font)',
        boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
      }}>
      <div style={{
        background: '#5a1010', padding: '6px 12px', position: 'relative',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />
        <span style={{ fontSize: 13 }}>⚡</span>
        <span style={{ color: '#ff9090', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Call Custom Event
        </span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>

      <div style={{ padding: '8px 12px' }}>
        <div style={{ fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>EVENT NAME</div>
        <input
          className="nodrag"
          list={listId}
          value={data?.name ?? ''}
          onChange={e => data.onChange?.('name', e.target.value)}
          placeholder="custom event…"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#120404', border: '1px solid #402020',
            color: '#ff9090', padding: '4px 7px', borderRadius: 3,
            fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
        <datalist id={listId}>
          {known.map(n => <option key={n} value={n} />)}
        </datalist>
        {name && !known.includes(name) && (
          <div className="node-desc" style={{ fontSize: 9, marginTop: 4 }}>
            No Custom Event named “{name}” yet — the call does nothing until one exists.
          </div>
        )}
      </div>
    </div>
  )
}
